import { useEffect, useState } from "react";
import type { Dispatch } from "react";

/**
 * Reads the value {@link useStickyState} keeps under `key`, for code that runs
 * outside a component — a route loader asking for what the page will ask for.
 */
export function readStickyState<T>(defaultValue: T, key: string): T {
  const stickyValue = window.localStorage.getItem(key);
  if (stickyValue === null) {
    return defaultValue;
  }
  try {
    return JSON.parse(stickyValue) as T;
  } catch {
    // The stored value predates JSON serialization or was otherwise
    // corrupted. Fall back to the default rather than crashing the app; the
    // hook's effect will overwrite it with a well-formed value.
    return defaultValue;
  }
}

export function useStickyState<T>(
  defaultValue: T,
  key: string,
): [T, Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState(() => readStickyState(defaultValue, key));

  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
}
